import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import maintenanceApi from "../api/maintenanceApi";
import type { PlannedMaintenanceFormData } from "../types";

export const maintenanceKeys = {
  status: ["maintenance-status"] as const,
  list: ["maintenance-list"] as const,
  detail: (id: string) => ["maintenance-detail", id] as const,
};

export function useMaintenanceStatus() {
  return useQuery({
    queryKey: maintenanceKeys.status,
    queryFn: maintenanceApi.getStatus,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function usePlannedMaintenanceList() {
  return useQuery({
    queryKey: maintenanceKeys.list,
    queryFn: maintenanceApi.list,
    staleTime: 30_000,
  });
}

export function useCreateMaintenance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: maintenanceApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.list });
      qc.invalidateQueries({ queryKey: maintenanceKeys.status });
    },
  });
}

export function useUpdateMaintenance(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<PlannedMaintenanceFormData>) => maintenanceApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.list });
      qc.invalidateQueries({ queryKey: maintenanceKeys.status });
    },
  });
}

export function useDeleteMaintenance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: maintenanceApi.remove,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.list });
      qc.invalidateQueries({ queryKey: maintenanceKeys.status });
    },
  });
}

export function useEndMaintenance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: maintenanceApi.end,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.status });
      qc.invalidateQueries({ queryKey: maintenanceKeys.list });
    },
  });
}

export function useExtendMaintenance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: maintenanceApi.extend,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.status });
      qc.invalidateQueries({ queryKey: maintenanceKeys.list });
    },
  });
}
